import { useEffect, useState } from 'react';
import { LuPencil, LuTrash2, LuSmartphone, LuUnplug } from 'react-icons/lu';
import RecordPanel, { PanelSection } from '../RecordPanel';
import SecaoHistorico from '../historico/SecaoHistorico';
import Select from '../Select';
import Dialog from '../Dialog';
import {
    getPlatformLabel, PlataformaEnum, StatusConexaoEnum, type ConexaoPlataforma,
} from '../../services/ConexaoPlataformaService';
import type { AgenteListItem } from '../../services/RegrasIAService';
import {
    DIAS_AVISO_EXPIRACAO, diasAte, formatarData, iniciaisDaPlataforma, lerConfig, rotuloStatus, classeStatus, temAtendimento,
} from './canais';

export interface UsuarioDaEmpresa { id: number; name: string }

export interface RascunhoConexao {
    nome: string;
    status: number;
    /** null = toda a equipe. */
    usuarioId: number | null;
    /** null = agente padrão da empresa. */
    agenteId: number | null;
}

interface Props {
    conexao: ConexaoPlataforma | null;
    modo: 'ver' | 'editar';
    onModo: (m: 'ver' | 'editar') => void;
    onClose: () => void;
    /** Só o administrador edita e remove; para os outros o painel é leitura. */
    admin: boolean;
    /** Multi-contas ligado na empresa: o campo Responsável existe. */
    multiContas: boolean;
    usuarios: UsuarioDaEmpresa[];
    agentes: AgenteListItem[];
    carregandoOpcoes: boolean;
    salvando: boolean;
    onSalvar: (r: RascunhoConexao) => Promise<void>;
    onRemover: () => Promise<void>;
    /** Só para o canal via QR Code: reabre o painel do código para parear de novo. */
    onReconectarQr?: () => void;
    /** Só para o canal via QR Code: desfaz o pareamento; a conexão continua, Inativa. */
    onDesconectarNumero?: () => Promise<void>;
}

const OPCOES_STATUS = [
    { value: String(StatusConexaoEnum.Ativa), label: 'Ativa' },
    { value: String(StatusConexaoEnum.Inativa), label: 'Inativa' },
    { value: String(StatusConexaoEnum.Expirada), label: 'Expirada' },
];

const TODA_EQUIPE = '__equipe__';
const AGENTE_PADRAO = '__padrao__';

/**
 * O painel de uma conexão — ver e editar no mesmo painel, como nas outras
 * telas de cadastro (desenho 2.3 da proposta). Substitui a modal de 600×668.
 */
const ConexaoPanel = ({
    conexao, modo, onModo, onClose, admin, multiContas, usuarios, agentes, carregandoOpcoes, salvando, onSalvar, onRemover,
    onReconectarQr, onDesconectarNumero,
}: Props) => {
    const [r, setR] = useState<RascunhoConexao>({ nome: '', status: StatusConexaoEnum.Ativa, usuarioId: null, agenteId: null });
    const [erroNome, setErroNome] = useState('');
    const [confirmar, setConfirmar] = useState(false);

    useEffect(() => {
        if (!conexao) return;
        setR({
            nome: conexao.nome,
            status: conexao.status,
            usuarioId: conexao.usuarioAtribuidoId,
            agenteId: conexao.usandoAgentePadrao ? null : conexao.agenteIaId,
        });
        setErroNome('');
    }, [conexao, modo]);

    if (!conexao) return null;

    const cfg = lerConfig(conexao);
    /* O secundário muda de sentido por canal: WABA no WhatsApp; no Instagram, a Página
       (login do Facebook) ou o id app-scoped da conta (login do Instagram). */
    const rotuloSecundario = conexao.plataforma !== PlataformaEnum.Instagram ? 'Conta (WABA)'
        : cfg.loginInstagram === 'instagram_login' ? 'ID no app' : 'Página (ID)';
    const dias = diasAte(conexao.tokenExpiracao);
    const expiraLogo = dias !== null && dias <= DIAS_AVISO_EXPIRACAO;
    const atende = temAtendimento(conexao);
    const viaQr = conexao.plataforma === PlataformaEnum.WhatsappEvolution;
    const nomeAgente = conexao.usandoAgentePadrao
        ? `${agentes.find(a => a.isDefault)?.nomeAgente ?? 'Agente'} · padrão da empresa`
        : (conexao.agenteIaNome || agentes.find(a => a.id === conexao.agenteIaId)?.nomeAgente || 'Agente específico');

    const salvar = async () => {
        if (!r.nome.trim()) { setErroNome('Informe um nome para a conexão.'); return; }
        await onSalvar({ ...r, nome: r.nome.trim() });
    };

    return (
        <>
            <RecordPanel
                open={!!conexao}
                onClose={onClose}
                initials={iniciaisDaPlataforma(conexao.plataforma)}
                squareAvatar
                title={modo === 'editar' ? (r.nome.trim() || conexao.nome) : conexao.nome}
                subtitle={getPlatformLabel(conexao.plataforma)}
                editing={modo === 'editar'}
                onBack={() => onModo('ver')}
                actions={admin && (
                    <>
                        <button type="button" className="rp-btn is-primary" onClick={() => onModo('editar')}>
                            <LuPencil size={13} /> Editar
                        </button>
                        {/* O canal via QR Code tem dois gestos que os da Meta não têm: parear de novo
                            (número desconectado) e desfazer o pareamento sem apagar a conexão. */}
                        {viaQr && conexao.status !== StatusConexaoEnum.Ativa && onReconectarQr && (
                            <button type="button" className="rp-btn" onClick={onReconectarQr}>
                                <LuSmartphone size={13} /> Ler QR Code
                            </button>
                        )}
                        {viaQr && conexao.status === StatusConexaoEnum.Ativa && onDesconectarNumero && (
                            <button type="button" className="rp-btn" onClick={onDesconectarNumero} disabled={salvando}>
                                <LuUnplug size={13} /> Desconectar número
                            </button>
                        )}
                    </>
                )}
                footer={
                    <>
                        <button type="button" className="rp-btn is-danger" onClick={() => setConfirmar(true)} disabled={salvando}>
                            <LuTrash2 size={13} /> Remover conexão
                        </button>
                        <button type="button" className="rp-btn" onClick={() => onModo('ver')} disabled={salvando}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                <PanelSection title="Canal">
                    <div className="rp-kv cx-kv">
                        <span className="rp-k">Plataforma</span><span className="rp-v">{getPlatformLabel(conexao.plataforma)}</span>
                        <span className="rp-k">Identificador</span><span className="rp-v cx-mono">{conexao.identificador}</span>
                        {conexao.identificadorSecundario && (
                            <><span className="rp-k">{rotuloSecundario}</span><span className="rp-v cx-mono">{conexao.identificadorSecundario}</span></>
                        )}
                        {cfg.loginInstagram && (
                            <><span className="rp-k">Conectada por</span>
                            <span className="rp-v">{cfg.loginInstagram === 'instagram_login' ? 'Login do Instagram' : 'Página do Facebook'}</span></>
                        )}
                        {cfg.usuarioInstagram && (
                            <><span className="rp-k">Usuário</span><span className="rp-v">@{cfg.usuarioInstagram}</span></>
                        )}
                        {cfg.paginaFacebook && (
                            <><span className="rp-k">Página do Facebook</span><span className="rp-v">{cfg.paginaFacebook}</span></>
                        )}
                        <span className="rp-k">Conectada em</span><span className="rp-v">{formatarData(conexao.conexaoCreatedat) || '—'}</span>
                        <span className="rp-k">Token expira em</span>
                        <span className={`rp-v ${expiraLogo ? 'cx-warn' : ''}`}>
                            {conexao.tokenExpiracao
                                ? <>{formatarData(conexao.tokenExpiracao)}{expiraLogo && dias !== null && <> · {dias < 0 ? 'vencido' : dias === 0 ? 'vence hoje' : `em ${dias} ${dias === 1 ? 'dia' : 'dias'}`}</>}</>
                                : <span className="lk-empty-value">não expira</span>}
                        </span>
                        {modo === 'ver' && (
                            <><span className="rp-k">Status</span>
                            <span className="rp-v"><span className={`cx-st ${classeStatus(conexao.status)}`}>{rotuloStatus(conexao.status)}</span></span></>
                        )}
                    </div>
                </PanelSection>

                {modo === 'ver' && atende && (
                    <PanelSection title="Atendimento">
                        <div className="rp-kv cx-kv">
                            <span className="rp-k">Responsável</span>
                            <span className="rp-v">{conexao.usuarioAtribuidoNome || 'Toda a equipe'}</span>
                            <span className="rp-k">Agente de IA</span>
                            <span className="rp-v">{nomeAgente}</span>
                        </div>
                    </PanelSection>
                )}

                {modo === 'editar' && (
                    <PanelSection title="Configuração">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="cx-nome">Nome</label>
                            <input id="cx-nome" className="rp-input" type="text" maxLength={255} autoFocus
                                value={r.nome} disabled={salvando}
                                onChange={e => { setR(x => ({ ...x, nome: e.target.value })); setErroNome(''); }} />
                            {erroNome && <span className="rp-error">{erroNome}</span>}
                        </div>
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="cx-status">Status</label>
                            <Select id="cx-status" value={String(r.status)} options={OPCOES_STATUS} disabled={salvando}
                                onChange={v => setR(x => ({ ...x, status: Number(v) }))} />
                        </div>
                        {atende && multiContas && (
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="cx-resp">Responsável</label>
                                <Select id="cx-resp" disabled={salvando || carregandoOpcoes}
                                    value={r.usuarioId === null ? TODA_EQUIPE : String(r.usuarioId)}
                                    options={[
                                        { value: TODA_EQUIPE, label: 'Toda a equipe' },
                                        ...usuarios.map(u => ({ value: String(u.id), label: u.name })),
                                    ]}
                                    onChange={v => setR(x => ({ ...x, usuarioId: v === TODA_EQUIPE ? null : Number(v) }))} />
                                <p className="rp-hint">Com multi-contas ligado na empresa, um usuário pode ser o dono desta conexão. As conversas novas caem para ele.</p>
                            </div>
                        )}
                        {atende && (
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="cx-agente">Agente de IA</label>
                                <Select id="cx-agente" disabled={salvando || carregandoOpcoes}
                                    value={r.agenteId === null ? AGENTE_PADRAO : String(r.agenteId)}
                                    options={[
                                        { value: AGENTE_PADRAO, label: 'Padrão da empresa' },
                                        ...agentes.filter(a => !a.isDefault).map(a => ({ value: String(a.id), label: a.nomeAgente })),
                                    ]}
                                    onChange={v => setR(x => ({ ...x, agenteId: v === AGENTE_PADRAO ? null : Number(v) }))} />
                                <p className="rp-hint">Quem responde por esta conexão. "Padrão" usa o agente da empresa.</p>
                            </div>
                        )}
                    </PanelSection>
                )}

                {/* Histórico (fase 5): nome, número, situação, responsável, agente. */}
                {modo === 'ver' && conexao && (
                    <SecaoHistorico entidade="conexao" id={conexao.conexaoPlataformaId} />
                )}
            </RecordPanel>

            <Dialog
                open={confirmar}
                onClose={() => setConfirmar(false)}
                title={`Remover ${conexao.nome}?`}
                confirmLabel="Remover conexão"
                danger
                busy={salvando}
                onConfirm={async () => { await onRemover(); setConfirmar(false); }}
            >
                As mensagens desse canal deixam de entrar no CRM. As conversas já registradas continuam no histórico.
            </Dialog>
        </>
    );
};

export default ConexaoPanel;
